import EventEmitter from 'events';
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  DefaultLoadingManager,
  DoubleSide,
  Intersection,
  Line3,
  LoadingManager,
  Matrix4,
  Mesh,
  Object3D,
  Ray,
  Side,
  Vector3,
} from 'three';
import {
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
  MeshBVH,
  StaticGeometryGenerator,
} from 'three-mesh-bvh';

import '../../shims/bvh.js';

const invMat = new Matrix4();
const aabb = new Box3();

export const applyCollider = (mesh: Mesh, force = true) => {
  const bvhMesh = mesh;

  if (!bvhMesh.geometry.computeBoundsTree) {
    bvhMesh.geometry.computeBoundsTree = computeBoundsTree.bind(bvhMesh.geometry);
    bvhMesh.geometry.disposeBoundsTree = disposeBoundsTree.bind(bvhMesh.geometry);
    bvhMesh.raycast = acceleratedRaycast.bind(bvhMesh);
  }

  if (force || !bvhMesh.geometry.boundsTree) {
    bvhMesh.geometry.computeBoundsTree();
  }

  return bvhMesh;
};

export const ensureBoundsTree = (geometry: BufferGeometry) => {
  const { boundsTree } = geometry;

  if (!boundsTree) {
    geometry.computeBoundsTree();
  }

  if (!boundsTree) {
    throw new Error('BoundsTree not found');
  }

  return boundsTree;
};

/**
 * Collider class for detecting collisions between objects.
 */
export class Collider extends EventEmitter {
  // TODO review this
  public source!: Object3D;
  public boundTree!: MeshBVH;
  public geometry!: BufferGeometry;
  public manager: LoadingManager;

  constructor(source?: Object3D, manager: LoadingManager = DefaultLoadingManager) {
    super();

    this.manager = manager;

    if (source) {
      this.setFromObject(source);
    }
  }

  /**
   * Set the collider from an Meshobject.
   */
  setFromObject(source: Object3D) {
    this.source = source;
    this.source.updateMatrixWorld(true);

    try {
      this.update();
    } catch (ex) {}
  }

  /**
   * Compute the geometry of the collider.
   */
  computeGeometry() {
    const generator = new StaticGeometryGenerator(this.source);
    generator.attributes = ['position'];
    this.geometry = generator.generate();
    return this;
  }

  /**
   * Update the collider.
   */
  update() {
    this.computeGeometry();
    this.boundTree = new MeshBVH(this.geometry, {
      onProgress: (progress) => {
        this.manager.onProgress('boundTree', progress * 100, 100);
      },
    });

    this.emit('update');

    return this;
  }

  /**
   * Detect if the collider intersects with another object.
   */
  intersects(object: Mesh) {
    const bvhMesh = applyCollider(object);

    invMat.copy(this.source.matrixWorld).invert().multiply(object.matrixWorld);

    return this.boundTree.intersectsGeometry(bvhMesh.geometry, invMat);
  }

  intersectsCollider(collider: Collider) {
    invMat.copy(this.source.matrixWorld).invert().multiply(collider.source.matrixWorld);

    return this.boundTree.intersectsGeometry(collider.geometry, invMat);
  }

  // TODO: review this
  contains(object: Mesh) {
    invMat.copy(object.matrixWorld).invert();
    const bvhMesh = applyCollider(object);

    bvhMesh.geometry.boundsTree?.getBoundingBox(aabb);

    aabb.applyMatrix4(object.matrixWorld);

    const result = this.boundTree.shapecast({
      intersectsBounds: (box) => aabb.intersectsBox(box),

      intersectsRange: (offset1, count1, contained1, depth1, nodeIndex1, box) => {
        aabb.copy(box);
        aabb.applyMatrix4(invMat);
        return (
          bvhMesh.geometry.boundsTree?.shapecast({
            intersectsBounds: (box) => aabb.intersectsBox(box),
            intersectsRange: (offset2, count2, contained2, depth2) => {
              return depth2 % 2 === 0;
            },
          }) ?? false
        );
      },
    });

    return result;
  }

  edgeIntersectionGeometry(mesh: Mesh, target: BufferGeometry = new BufferGeometry()) {
    const bvhMesh = applyCollider(mesh);

    if (!bvhMesh.geometry.boundsTree) {
      throw new Error('BoundsTree not found');
    }

    const matrix2to1 = new Matrix4()
      .copy(this.source.matrixWorld)
      .invert()
      .multiply(bvhMesh.matrixWorld);

    const edge = new Line3();

    const results: number[] = [];

    const geometry = target;
    const position = new BufferAttribute(new Float32Array(2 * 3), 3);
    geometry.setAttribute('position', position);
    geometry.setFromPoints([new Vector3(0, 1, 0), new Vector3(0, -1, 0)]);

    this.boundTree.bvhcast(bvhMesh.geometry.boundsTree, matrix2to1, {
      intersectsTriangles(triangle1, triangle2) {
        if (triangle1.intersectsTriangle(triangle2, edge)) {
          const { start, end } = edge;
          results.push(start.x, start.y, start.z, end.x, end.y, end.z);
        }

        return false;
      },
    });

    const posArray = position.array;

    if (posArray.length < results.length) {
      geometry.dispose();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(results), 3, false));
    } else {
      // @ts-ignore
      posArray.set(results);
    }

    geometry.setDrawRange(0, results.length / 3);
    geometry.attributes.position.needsUpdate = true;

    return geometry;
  }

  /**
   * Detect if the collider intersects with a ray.
   */
  rayIntersect(ray: Ray, side: Side = DoubleSide): Intersection | null {
    return this.boundTree.raycastFirst(ray, side);
  }
}

export default Collider;
